import { useState, useEffect, useRef } from 'react';
import { X, ClipboardList } from 'lucide-react';
import { useApp } from './context/AppContext';
import { useSocket } from './context/SocketContext';
import Sidebar from './components/Sidebar/Sidebar';
import IconRail, { SidebarTab } from './components/Sidebar/IconRail';
import ChatView from './components/Chat/ChatView';
import ThreadPanel from './components/Chat/ThreadPanel';
import CreateChannelModal from './components/Modals/CreateChannelModal';
import CreateTicketModal from './components/Modals/CreateTicketModal';
import CreateProjectModal from './components/Modals/CreateProjectModal';
import CreateTopicModal from './components/Modals/CreateTopicModal';
import CreateDMModal from './components/Modals/CreateDMModal';
import SettingsModal from './components/Modals/SettingsModal';
import { AdminView } from './components/Admin/AdminView';
import TicketCalendar from './components/Tickets/TicketCalendar';
import TicketDashboard from './components/Tickets/TicketDashboard';
import TicketBoard from './components/Tickets/TicketBoard';
import ProjectList from './components/Projects/ProjectList';
import { DepartmentQueueView } from './components/Tickets/DepartmentQueueView';
import LoginPage from './components/Auth/LoginPage';
import SetupPasswordPage from './components/Auth/SetupPasswordPage';
import ForgotPasswordModal from './components/Auth/ForgotPasswordModal';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import TwoFactorPage from './components/Auth/TwoFactorPage';
import OAuthRecoveryPage from './components/Auth/OAuthRecoveryPage';
import { UpdateBanner } from './components/Desktop/UpdateBanner';
import OAuthRecoveryBanner from './components/Auth/OAuthRecoveryBanner';

// Auth view types
type AuthView = 'login' | 'setup-password' | 'forgot-password' | 'oauth-recovery' | 'reset-password' | '2fa' | null;

import { MobileMenuContext, useMobileMenu } from './context/MobileMenuContext';

export { useMobileMenu };

function App() {
    const { isLoading, activeView, activeThread, currentUser, setActiveView, requestNotificationPermission, hasPermission, departments, sendNotification } = useApp();
    const { socket } = useSocket();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<SidebarTab>('chat');

    // Toast state
    const [toasts, setToasts] = useState<{ id: string, title: string, message: string, onClick?: () => void }[]>([]);

    // Use a ref for activeView so socket handler doesn't go stale without re-registering
    const activeViewRef = useRef(activeView);
    useEffect(() => { activeViewRef.current = activeView; }, [activeView]);

    useEffect(() => {
        if (!socket || !currentUser) return;

        const handleTicketAssigned = (ticket: any) => {
            const uid = currentUser.id;

            // The server tells us exactly who the relevant parties are
            const amAssigned = ticket.assignees?.some((a: any) => a.id === uid);
            const amCollaborator = ticket.collaborators?.some((c: any) => c.id === uid);
            const amDeptHead = (ticket._dept_head_ids || []).includes(uid);
            const amCreator = ticket._creator_id === uid;

            // Determine the right message
            let messageStr = '';
            if (amAssigned) {
                messageStr = `You were assigned ticket #${ticket.ticket_number || ticket.id.slice(0, 6)}: ${ticket.title}`;
            } else if (amCollaborator) {
                messageStr = `You were added as a collaborator on ticket #${ticket.ticket_number || ticket.id.slice(0, 6)}: ${ticket.title}`;
            } else if (amDeptHead) {
                const deptName = departments.find(d => d.id === (ticket.assigned_to_department_id || ticket.department_id))?.name || 'your department';
                messageStr = `New ticket in ${deptName} queue — please delegate.`;
            } else if (amCreator) {
                // Creator doesn't need an alert — they just created it
                return;
            } else {
                // Not relevant to this user
                return;
            }

            // Skip notification if already viewing this ticket and window is focused
            if (activeViewRef.current.type === 'ticket' && activeViewRef.current.id === ticket.id && !document.hidden) return;

            sendNotification(ticket.title || 'Ticket Update', {
                body: messageStr,
                silent: false
            });
        };

        socket.on('ticket_assigned', handleTicketAssigned);
        return () => {
            socket.off('ticket_assigned', handleTicketAssigned);
        };
    }, [socket, currentUser, departments, activeView]);

    const handleTabChange = (tab: SidebarTab) => {
        setActiveTab(tab);
        if (tab === 'chat') {
            setActiveView({ type: 'inbox', id: 'inbox' });
        } else if (tab === 'work') {
            if (hasPermission('view:ticket_dashboard')) {
                setActiveView({ type: 'ticket-dashboard', id: 'dashboard' });
            } else {
                setActiveView({ type: 'tickets-list', id: 'all' });
            }
        } else if (tab === 'admin') {
            setActiveView({ type: 'admin', id: 'admin' });
        }
    };

    // Auth state
    const [authView, setAuthView] = useState<AuthView>(null);
    const [authEmail, setAuthEmail] = useState('');
    const [authInviteToken, setAuthInviteToken] = useState<string | undefined>();
    const [authResetToken, setAuthResetToken] = useState('');
    const [auth2FAUserId, setAuth2FAUserId] = useState('');
    const [auth2FAPassword, setAuth2FAPassword] = useState('');

    // Check URL for auth tokens on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const resetToken = params.get('reset_token');
        const inviteToken = params.get('token');
        const email = params.get('email');

        if (resetToken) {
            setAuthResetToken(resetToken);
            setAuthView('reset-password');
        } else if (inviteToken && email) {
            setAuthEmail(decodeURIComponent(email));
            setAuthInviteToken(inviteToken);
            setAuthView('setup-password');
        }
    }, []);

    const mobileMenuValue = {
        isOpen: mobileMenuOpen,
        toggle: () => setMobileMenuOpen(!mobileMenuOpen),
        close: () => setMobileMenuOpen(false)
    };

    const handleLoginSuccess = async () => {
        setAuthView(null);
        // Only clear URL params via history if we're not in Electron,
        // as history.replaceState on file:// protocol can be unpredictable.
        if (!navigator.userAgent.includes('Electron')) {
            window.history.replaceState(window.history.state || {}, '', window.location.pathname);
        }
    };

    const handleNeedsSetup = (email: string, token: string) => {
        setAuthEmail(email);
        setAuthInviteToken(token);
        setAuthView('setup-password');
    };

    const handleNeeds2FA = (userId: string, password?: string) => {
        setAuth2FAUserId(userId);
        setAuth2FAPassword(password || '');
        setAuthView('2fa');
    };

    // 1. Show loading state first to allow session restoration
    if (isLoading) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        fontSize: 48,
                        marginBottom: 'var(--spacing-md)',
                        animation: 'pulse 1.5s infinite'
                    }}>💬</div>
                    <h2 style={{ fontWeight: 600 }}>Loading Tock...</h2>
                </div>
            </div>
        );
    }

    // 2. Show auth pages if not authenticated OR explicitly in an auth view (like reset)
    if (!currentUser || authView) {
        if (authView === 'setup-password') {
            return (
                <SetupPasswordPage
                    email={authEmail}
                    inviteToken={authInviteToken}
                    onSuccess={() => {
                        requestNotificationPermission();
                        handleLoginSuccess();
                    }}
                    onBack={() => setAuthView('login')}
                />
            );
        }

        if (authView === 'forgot-password') {
            return (
                <ForgotPasswordModal
                    onBack={() => setAuthView('login')}
                    onOAuthRecovery={() => setAuthView('oauth-recovery')}
                />
            );
        }

        if (authView === 'oauth-recovery') {
            return (
                <OAuthRecoveryPage
                    onRecovered={() => setAuthView('reset-password')}
                    onBack={() => setAuthView('forgot-password')}
                />
            );
        }

        if (authView === 'reset-password') {
            return (
                <ResetPasswordPage
                    token={authResetToken}
                    onSuccess={() => setAuthView('login')}
                />
            );
        }

        if (authView === '2fa') {
            return (
                <TwoFactorPage
                    userId={auth2FAUserId}
                    password={auth2FAPassword}
                    onSuccess={handleLoginSuccess}
                    onBack={() => setAuthView('login')}
                />
            );
        }

        // Default: show login
        return (
            <LoginPage
                onLoginSuccess={handleLoginSuccess}
                onNeedsSetup={handleNeedsSetup}
                onNeeds2FA={handleNeeds2FA}
                onForgotPassword={() => setAuthView('forgot-password')}
            />
        );
    }

    return (
        <MobileMenuContext.Provider value={mobileMenuValue}>
            <div className="app-layout">
                {/* Mobile sidebar overlay */}
                <div
                    className={`sidebar-overlay ${mobileMenuOpen ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                />

                {/* Icon Rail */}
                <IconRail activeTab={activeTab} onTabChange={handleTabChange} />

                {/* Sidebar */}
                <Sidebar className={mobileMenuOpen ? 'open' : ''} activeTab={activeTab} />

                {/* Main content */}
                <main className="main-content">
                    {(() => {
                        const viewType = activeView.type;
                        if (viewType === 'admin') return <AdminView />;
                        if (viewType === 'ticket-calendar') return <TicketCalendar />;
                        if (viewType === 'ticket-board') return <TicketBoard />;
                        if (viewType === 'project-list') return <ProjectList />;
                        if (viewType === 'ticket-dashboard') {
                            if (hasPermission('view:ticket_dashboard')) {
                                return <TicketDashboard />;
                            }
                            // Fallback if accessed via direct manipulation or old state
                            setActiveView({ type: 'tickets-list', id: 'all' });
                            return null;
                        }
                        if (viewType === 'dept-queue') return <DepartmentQueueView />;
                        return (
                            <>
                                <ChatView />
                                {activeThread && <ThreadPanel />}
                            </>
                        );
                    })()}
                </main>

                {/* Modals */}
                <CreateChannelModal />
                <CreateTicketModal />
                <CreateProjectModal />
                <CreateTopicModal />
                <CreateDMModal />
                <SettingsModal />
                {/* Desktop auto-update banner */}
                <UpdateBanner />
                {/* OAuth recovery prompt for users who haven't linked yet */}
                <OAuthRecoveryBanner />

                {/* Toasts overlay */}
                {toasts.length > 0 && (
                    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {toasts.map(toast => (
                            <div key={toast.id} onClick={toast.onClick} style={{
                                background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                                borderRadius: 8, padding: '12px 16px', minWidth: 280, maxWidth: 350,
                                boxShadow: 'var(--shadow-lg)', cursor: toast.onClick ? 'pointer' : 'default',
                                display: 'flex', alignItems: 'flex-start', gap: 12,
                                transform: 'translateY(0)', transition: 'all 0.3s ease-out'
                            }}>
                                <div style={{ background: 'var(--accent-primary)', color: 'white', borderRadius: '50%', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ClipboardList size={16} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{toast.title}</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{toast.message}</div>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setToasts(prev => prev.filter(t => t.id !== toast.id)); }}
                                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </MobileMenuContext.Provider>
    );
}

export default App;
